// The card payment's localStorage record (redirect fallback). Renamed from the
// Worldpay-only key; both are read for one release so an in-flight payment survives the deploy.
// One slot is shared by every venue this browser orders from.
const PENDING_KEY = 'card_pending_payment';
const LEGACY_KEY = 'worldpay_pending_payment';

export function readPendingPayment() {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || localStorage.getItem(LEGACY_KEY) || 'null');
  } catch {
    return null;
  }
}

// Saves the record and drops any legacy-key record (so a stale one can never resurface once
// this one is compare-cleared). Never overwrites ANOTHER venue's confirming record (that
// customer may have been charged): returns false and writes nothing.
export function savePendingPayment(record) {
  const current = readPendingPayment();
  if (current && current.confirming && current.venue_id && record && record.venue_id
      && String(current.venue_id) !== String(record.venue_id)) {
    return false;
  }
  localStorage.setItem(PENDING_KEY, JSON.stringify(record));
  localStorage.removeItem(LEGACY_KEY);
  return true;
}

export function clearPendingPayment() {
  localStorage.removeItem(PENDING_KEY);
  localStorage.removeItem(LEGACY_KEY);
}

// Clear the record only if it is a plain one (a session was created but executePayment was
// never called). A confirming record (the customer may have been charged) is kept.
export function clearPlainPendingPayment() {
  const rec = readPendingPayment();
  if (rec && rec.confirming) return;
  clearPendingPayment();
}

// Compare-and-clear: remove only the record(s) that still belong to `reference`, so a late
// answer about an old attempt never erases a newer attempt's record.
export function clearPendingPaymentFor(reference) {
  for (const key of [PENDING_KEY, LEGACY_KEY]) {
    try {
      const rec = JSON.parse(localStorage.getItem(key) || 'null');
      if (rec && rec.payment_reference === reference) localStorage.removeItem(key);
    } catch {
      // unreadable record: leave it
    }
  }
}

// Compare-and-clear for a PLAIN record only: a record for `reference` that has since become
// confirming (the customer may have been charged) is kept.
export function clearPlainPendingPaymentFor(reference) {
  for (const key of [PENDING_KEY, LEGACY_KEY]) {
    try {
      const rec = JSON.parse(localStorage.getItem(key) || 'null');
      if (rec && rec.payment_reference === reference && !rec.confirming) localStorage.removeItem(key);
    } catch {
      // unreadable record: leave it
    }
  }
}

// The confirming record (executePayment has been called, so the customer may have been
// charged) for this venue, or null.
export function readConfirmingPaymentFor(venueId) {
  const rec = readPendingPayment();
  return rec && rec.confirming && rec.payment_reference
    && (!rec.venue_id || String(rec.venue_id) === String(venueId))
    ? rec
    : null;
}

// A confirming record that belongs to a DIFFERENT venue, or null. While it exists no card
// attempt may start here: it would need the one storage slot that protects that payment.
export function readConfirmingPaymentElsewhere(venueId) {
  const rec = readPendingPayment();
  return rec && rec.confirming && rec.payment_reference
    && rec.venue_id && String(rec.venue_id) !== String(venueId)
    ? rec
    : null;
}

// The only error answers that end a confirming attempt: 409 { restart: true } (the attempt is
// over and was retired) and 404 "Payment record not found" (there is no such payment). Any
// other error (400, 503, network, mismatch, a route-level 404) leaves the outcome unknown.
export function isDefinitiveEnd(err) {
  if (!err) return false;
  if (err.status === 409 && err.body && err.body.restart) return true;
  return err.status === 404 && /payment record not found/i.test((err.body && err.body.error) || '');
}
